import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Package, Users, DollarSign, Eye } from "lucide-react";
import { TID } from "@/constants/tid";
import AdminWaitlistTable from "@/components/AdminWaitlistTable";
import TeamPanel from "@/components/TeamPanel";
import ChannelPerformance from "@/components/ChannelPerformance";
import CampaignPerformance from "@/components/CampaignPerformance";
import VisitorLog from "@/components/VisitorLog";
import MembersPanel from "@/components/MembersPanel";
import AccountsPanel from "@/components/AccountsPanel";
import AdsManagerPanel from "@/components/AdsManagerPanel";
import PartnersPanel from "@/components/PartnersPanel";
import TelegramPosterCard from "@/components/TelegramPosterCard";
import XPosterCard from "@/components/XPosterCard";
import MetaPosterCard from "@/components/MetaPosterCard";
import { EmailHealthCard } from "@/components/EmailHealthCard";
import { InboundReplyCard } from "@/components/InboundReplyCard";
import { FunnelCard } from "@/components/FunnelCard";
import ActivityLog from "@/components/ActivityLog";
import LaunchSettingsCard from "@/components/LaunchSettingsCard";
import { ReportsPanel } from "@/components/ReportsPanel";
import { CatalogHealthCard } from "@/components/CatalogHealthCard";
import { CampaignKitCard } from "@/components/CampaignKitCard";
import { MarketingAutopilotCard } from "@/components/MarketingAutopilotCard";
import { SpotlightAdminCard } from "@/components/SpotlightAdminCard";
import { CampaignLinksCard } from "@/components/CampaignLinksCard";
import { UptimeMonitorCard } from "@/components/UptimeMonitorCard";
import { TrustQueueCard } from "@/components/TrustQueueCard";
import { GoogleFeedCard } from "@/components/GoogleFeedCard";
import { BackupCard } from "@/components/BackupCard";
import { DriveArchiveCard } from "@/components/DriveArchiveCard";
import VisibilityIntelligence from "@/components/VisibilityIntelligence";
import { CountryHeatmap } from "@/components/CountryHeatmap";
import { TrafficOverview } from "@/components/TrafficOverview";
import { PaymentsHealthCard } from "@/components/PaymentsHealthCard";
import { OfficialStagePanel } from "@/components/OfficialStagePanel";
import { OfficialArchiveCard } from "@/components/OfficialArchiveCard";
import { MarketingKitCard } from "@/components/MarketingKitCard";
import { MarketingCaptionsCard } from "@/components/MarketingCaptionsCard";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/analytics/admin");
      setS(data);
    })();
  }, []);

  if (!s) return <div className="p-10 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Platform overview</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Admin control room</h1>

      <div data-testid={TID.adminStats} className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Package} label="Live products" value={s.live_products} accent="text-[#34C759]" sub={`${s.total_products} total`} />
        <Stat icon={Users} label="Users" value={s.total_users} sub={`${s.sellers} sellers &middot; ${s.buyers} buyers`} />
        <Stat icon={Eye} label="Views 24h" value={s.views_24h} />
        <Stat icon={DollarSign} label="GMV 24h" value={`$${s.gmv_24h.toFixed(2)}`} accent="text-[#007AFF]" sub={`${s.orders_24h} orders`} />
        <Stat
          icon={DollarSign}
          label="Platform fees due"
          value={`$${(s.platform_fees_owed_total || 0).toFixed(2)}`}
          accent="text-[#FF3B30]"
          sub={`0.5% per item sold \u00B7 +$${(s.platform_fees_24h || 0).toFixed(2)} last 24h`}
        />
      </div>

      <TrafficOverview />

      <div className="mt-10"><PaymentsHealthCard /></div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-[#27272A] bg-[#121212] p-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Orders by hour &mdash; last 24h</div>
          <h3 className="font-black text-xl tracking-tight mt-1">Order velocity</h3>
          <div className="mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.hourly}>
                <CartesianGrid stroke="#27272A" vertical={false} />
                <XAxis dataKey="hour" stroke="#71717a" fontSize={11} tick={{ fontFamily: "monospace" }} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A" }} labelStyle={{ color: "#fff" }} />
                <Bar dataKey="orders" fill="#007AFF" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-[#27272A] bg-[#121212] p-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Live category mix</div>
          <h3 className="font-black text-xl tracking-tight mt-1">Live products by category</h3>
          <ul className="mt-4 space-y-3">
            {s.categories.map((c, i) => (
              <li key={c.category || i} className="flex items-center gap-3">
                <span className="w-24 text-sm">{c.category || "uncategorized"}</span>
                <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#007AFF]" style={{ width: `${Math.min(100, (c.count / Math.max(1, s.categories[0].count)) * 100)}%` }} />
                </div>
                <span className="font-mono tabular-nums text-sm">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {user?.role === "admin" && <FunnelCard />}

      {user?.role === "admin" && (
        <div className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-5" data-testid="admin-quick-actions">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Do everything a seller can — as admin</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {[["/seller/stage", "My Stage (Studio, go live, all 8 Hostings)"], ["/seller", "Seller dashboard"], ["/seller/new", "Add a product"], ["/seller/bulk", "Bulk upload"], ["/stages", "Public Stages"], ["/payouts", "Payouts & ID"], ["/orders", "Orders & enquiries"], ["/wanted", "Wanted posts"]].map(([to, label]) => (
              <Link key={to} to={to} data-testid={`admin-link-${to.replace(/\//g, "-").slice(1)}`} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-neutral-200 hover:border-white hover:bg-white hover:text-black transition-colors">{label}</Link>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-neutral-500">Admin accounts hold every Hosting free, skip the seller plan and ID gates, and can publish, go live and sell from their own Stage.</p>
        </div>
      )}

      {user?.role === "admin" && <TrustQueueCard />}

      {user?.role === "admin" && <AccountsPanel />}
      {user?.role === "admin" && <MembersPanel />}

      {user?.role === "admin" && <CatalogHealthCard />}
      {user?.role === "admin" && <GoogleFeedCard />}
      {user?.role === "admin" && <BackupCard />}
      {user?.role === "admin" && <DriveArchiveCard />}

      {user?.role === "admin" && <VisibilityIntelligence />}
      {user?.role === "admin" && <CountryHeatmap />}

      {user?.role === "admin" && <MarketingAutopilotCard />}
      {user?.role === "admin" && <SpotlightAdminCard />}
      {user?.role === "admin" && <CampaignKitCard />}
      {user?.role === "admin" && <OfficialStagePanel />}
      {user?.role === "admin" && <MarketingKitCard />}
      {user?.role === "admin" && <MarketingCaptionsCard />}
      {user?.role === "admin" && <OfficialArchiveCard />}

      {user?.role === "admin" && <UptimeMonitorCard />}

      {user?.role === "admin" && <CampaignLinksCard />}

      {user?.role === "admin" && <ReportsPanel />}

      {user?.role === "admin" && <AdsManagerPanel />}

      {user?.role === "admin" && <TelegramPosterCard />}

      {user?.role === "admin" && <XPosterCard />}
      {user?.role === "admin" && <MetaPosterCard />}
      {user?.role === "admin" && <EmailHealthCard />}
      {user?.role === "admin" && <InboundReplyCard />}

      {user?.role === "admin" && <PartnersPanel />}

      <VisitorLog />
      <CampaignPerformance />
      <ChannelPerformance />

      <div className="mt-10">
        <AdminWaitlistTable canInvite={user?.role === "admin"} />
      </div>

      {user?.role === "admin" && <LaunchSettingsCard />}
      <ActivityLog />
      {user?.is_root && <TeamPanel />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
        <Icon className="h-4 w-4 text-neutral-500" />
      </div>
      <div className={`mt-3 font-mono tabular-nums font-bold text-3xl ${accent || "text-white"}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1" dangerouslySetInnerHTML={{ __html: sub }} />}
    </div>
  );
}
